import type { Compendium } from "@lancer-vtt/compendium";
import type { FrozenStructureTable } from "@lancer-vtt/domain";

/**
 * The pack id of the Monstrosity Structure Damage table, the one pack table `../monstrosityStructure.ts`
 * resolves. Declared here, not there: the spawn path must not import the resolver, whose import
 * graph reaches `tables.ts` and the effects barrel and so closes a load-order cycle through
 * `effects/sidecar.ts` → `spawn.ts` (`lint.ts` then reads `INTRINSIC_STATUS_NAMES` before
 * `sidecar.ts` has initialized it).
 */
export const MONSTROSITY_STRUCTURE_TABLE = "core-monstrosities";

/**
 * The NPC features that name a pack table as their carrier's structure damage table, keyed by
 * feature id. The link lives only in prose (Unique Physiology: "it rolls on the Monstrosity
 * Structure Damage Table table when it takes structure damage"), so it is written down here.
 * Only what the shipped pack carries is wired: one feature, one table.
 */
const STRUCTURE_TABLE_BY_FEATURE: Readonly<Record<string, string>> = {
  npcf_unique_physiology_monstrosity: MONSTROSITY_STRUCTURE_TABLE,
};

/**
 * Freeze the structure damage table one of `featureIds` names, read from the compendium's
 * `rollable_table`s. `undefined` when no feature names one, or when the named table is not in a
 * loaded pack (the NPC then rolls the core mech table, as it did before the table was ingested).
 */
export function freezeStructureTable(args: { featureIds: readonly string[]; compendium: Compendium }): FrozenStructureTable | undefined {
  const { featureIds, compendium } = args;
  for (const featureId of featureIds) {
    const tableId = STRUCTURE_TABLE_BY_FEATURE[featureId];
    if (tableId === undefined) continue;
    const table = compendium.getById("rollable_table", tableId);
    if (table === undefined) continue;
    return {
      id: table.id,
      title: table.title,
      results: table.results.map((r) => ({ min: r.min, max: r.max, title: r.title ?? table.title, result: r.result })),
    };
  }
  return undefined;
}
